import { OldBrowserModal } from "@monta/components";
import { ToastProvider } from "@monta/design-system";
import { BrowserRouter, Route, Routes } from "@monta/router-react";
import type { TUtils } from "@monta/utils";
import { useExamChecker, UsersTypeEnum, withPolling } from "@monta/utils";
import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import ExamResultsPackagesView from "./components/exam-results/views/package";
import ExamResultsType1View from "./components/exam-results/views/type-1";
import ExamResultsType2View from "./components/exam-results/views/type-2";
import ExamResultsType3View from "./components/exam-results/views/type-3";
import Layout from "./components/layout";
import EmptyLayout from "./components/layout/EmptyLayout";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import Loading from "./components/loading";
import { LoadingProvider } from "./components/loading/provider";
import Cart from "./pages/cart";
import ExamMarketManagement from "./pages/exam-market/management";
import AppJotaiProvider from "./providers/AppJotaiProvider";
import AuthProvider from "./providers/AuthProvider";
import * as utils from "./utils";
import { auth } from "./utils";
import { UserProvider } from "./utils/hooks/useUser";
import { containsUserType } from "./utils/users/usersType";

const Dashboard = lazy(() => import("./pages/dashboard"));

const ExamManagementStudent = lazy(
  () => import("./pages/exam-management/student")
);
const MyPackages = lazy(() => import("./pages/my-packages"));
const Custom404 = lazy(() => import("./pages/404"));
const TextBookPage = lazy(() => import("./pages/text-book"));
const VideoPage = lazy(() => import("./pages/product/video"));
const OneTimePassword = lazy(
  () => import("./pages/auth/login/one-time-password")
);
const ForgetPassword = lazy(() => import("./pages/auth/forget"));
const Register = lazy(() => import("./pages/auth/register"));
const PhoneNumberPage = lazy(
  () => import("./pages/auth/register/phone-number")
);
const OtpPage = lazy(() => import("./pages/auth/otp"));
const AlreadySignedUpPage = lazy(
  () => import("./pages/auth/register/already-signed-up")
);
const CompletionForm = lazy(() => import("./pages/auth/register/completion"));
const LoginPage = lazy(() => import("./pages/auth/login"));
const ChangePasswordPage = lazy(() => import("./pages/auth/forget/change"));
const EditProfile = lazy(() => import("./pages/profile/edit"));
const AccountList = lazy(() => import("./pages/auth/account-list"));
const ExamLauncherCheckout = lazy(
  () => import("./pages/exam-launcher/checkout")
);
const BankQuickAction = lazy(() => import("./pages/product/bank"));
const ManageDescriptiveExamsPage = lazy(
  () => import("./pages/manage-descriptive-exams")
);
const SubmitMarksPage = lazy(() => import("./pages/descriptive-exam-sheet"));
const ProvidersDashboard = lazy(() => import("./pages/providers/dashboard"));
const ProvidersDashboardDetails = lazy(
  () => import("./pages/providers/dashboard/details")
);
const Affiliate = lazy(() => import("./pages/affiliate"));
const Affiliate2 = lazy(() => import("./pages/affiliate2"));
const AffiliateDashboard = lazy(() => import("./pages/affiliate/dashboard"));
const EducationPackOverall = lazy(
  () => import("./pages/product/educationPack/overall")
);
const EducationPackChapter = lazy(
  () => import("./pages/product/educationPack/chapter")
);
const ExamResultsPage = lazy(() => import("./pages/exam-results"));
const MontiPage = lazy(() => import("./pages/ai"));
const MontiChatPage = lazy(() => import("./pages/ai/monti-chat"));
const CartPayment = lazy(() => import("./pages/cart/payment"));
const Payment = lazy(() => import("./pages/payment/result"));
const ActivateContract = lazy(() => import("./pages/activate-contract"));
const LoginWithToken = lazy(() => import("./pages/auth/login-with-token"));
const ExamManagementSchool = lazy(
  () => import("./pages/exam-management/school")
);

const ExamManagementSimplified = lazy(
  () => import("./pages/exam-management/simplified")
);

const OnlineSelfExamGeneratorPage = lazy(
  () => import("./pages/online-self-exam-generator")
);

const affiliateAccess = [
  UsersTypeEnum.ADVISOR,
  UsersTypeEnum.ADMINISTRATOR,
  UsersTypeEnum.DEAN,
  UsersTypeEnum.OPERATOR,
  UsersTypeEnum.AUTHOR,
  UsersTypeEnum.OBSERVER,
  UsersTypeEnum.AGENT,
  UsersTypeEnum.TEACHER,
  UsersTypeEnum.SALES_MANAGER,
  UsersTypeEnum.SALES_EXPERT,
  UsersTypeEnum.PARENT,
  UsersTypeEnum.SCHOOLOPERATOR,
  UsersTypeEnum.PRESENTER,
  UsersTypeEnum.آموزشگاه,
  UsersTypeEnum.مرکز_مشاوره,
  UsersTypeEnum.AM,
  UsersTypeEnum.SDR,
  UsersTypeEnum.مدیر_مالی,
  UsersTypeEnum.کارشناس_مالی,
  UsersTypeEnum.SUPER_USER,
  UsersTypeEnum.PUBLISHER,
  UsersTypeEnum.معاون_مدرسه,
  UsersTypeEnum.MARKETING_MANAGER,
  UsersTypeEnum.MARKETING,
  UsersTypeEnum.EXAM_MARKET_ADMIN,
  UsersTypeEnum.EXAM_MARKET_PROVIDER
];

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false
      }
    }
  });

  const user = auth.useAuth();
  useExamChecker(utils as TUtils);

  return (
    <Suspense fallback={<Loading />}>
      <BrowserRouter>
        <LoadingProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <UserProvider>
                <AppJotaiProvider>
                  <Routes>
                    <Route
                      path="/exam-management/simplified"
                      element={<ExamManagementSimplified />}
                    />

                    <Route element={<Layout />}>
                      <Route path="/" element={<Loading />} />
                      <Route path="/cart" element={<Cart />} />

                      <Route
                        element={
                          <ProtectedRoute
                            isAllowed={containsUserType(
                              user?.typeId,
                              UsersTypeEnum.ADMINISTRATOR,
                              UsersTypeEnum.STUDENT
                            )}
                          />
                        }
                      >
                        <Route path="/monti" element={<MontiPage />} />

                        <Route path="/monti-chat" element={<MontiChatPage />} />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute
                            isAllowed={containsUserType(
                              user?.subTypeId,
                              UsersTypeEnum.STUDENT,
                              UsersTypeEnum.PARENT
                            )}
                          />
                        }
                      >
                        <Route
                          path="/exam-management/student"
                          element={<ExamManagementStudent />}
                        />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/my-packages" element={<MyPackages />} />
                        <Route
                          path="/text-book/:packId/:id/:itemId/:name"
                          element={<TextBookPage />}
                        />

                        <Route
                          path="/product/video/:id/:itemId"
                          element={<VideoPage />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute isAllowed={Boolean(user?.id)} />
                        }
                      >
                        <Route
                          path="/product/education-pack/:packageId"
                          element={<EducationPackOverall />}
                        />

                        <Route
                          path="/product/education-pack/chapter/:packageId"
                          element={<EducationPackChapter />}
                        />

                        <Route
                          path="/product/bank"
                          element={<BankQuickAction />}
                        />

                        <Route path="/profile">
                          <Route path="edit" element={<EditProfile />} />
                          <Route
                            path="account-list"
                            element={<AccountList />}
                          />
                        </Route>
                      </Route>

                      {/* کارنامه */}
                      {/* Result page */}
                      <Route
                        element={
                          <ProtectedRoute isAllowed={Boolean(user?.id)} />
                        }
                      >
                        <Route
                          path="/exam-results"
                          element={<ExamResultsPage />}
                        >
                          <Route
                            path="/exam-results/package/:tabId/:examTakingId"
                            element={<ExamResultsPackagesView />}
                          />
                          <Route
                            path="/exam-results/type-1/:tabId/:examTakingId"
                            element={<ExamResultsType1View />}
                          />
                          <Route
                            path="/exam-results/type-2/:tabId/:examTakingId"
                            element={<ExamResultsType2View />}
                          />
                          <Route
                            path="/exam-results/type-3/:tabId/:examTakingId"
                            element={<ExamResultsType3View />}
                          />
                        </Route>
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute
                            isAllowed={containsUserType(
                              user?.typeId,
                              ...affiliateAccess
                            )}
                          />
                        }
                      >
                        <Route path="/affiliate2" element={<Affiliate2 />} />
                        <Route path="/affiliate" element={<Affiliate />} />

                        <Route
                          path="/affiliate/dashboard"
                          element={<AffiliateDashboard />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute
                            isAllowed={containsUserType(
                              user?.subTypeId,
                              UsersTypeEnum.EXAM_MARKET_PROVIDER
                            )}
                          />
                        }
                      >
                        <Route
                          path="/providers/dashboard"
                          element={<ProvidersDashboard />}
                        />

                        <Route
                          path="/providers/dashboard/details"
                          element={<ProvidersDashboardDetails />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute
                            isAllowed={
                              containsUserType(
                                user?.subTypeId,
                                UsersTypeEnum.EXAM_MARKET_ADMIN,
                                UsersTypeEnum.EXAM_MARKET_PROVIDER,
                                UsersTypeEnum.ADMINISTRATOR
                              ) || user?.id == 1120727
                            }
                          />
                        }
                      >
                        <Route path="/exam-market">
                          <Route
                            path="management"
                            element={<ExamMarketManagement />}
                          />
                        </Route>
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute
                            isAllowed={containsUserType(
                              user?.subTypeId,
                              UsersTypeEnum.ADVISOR,
                              UsersTypeEnum.ADMINISTRATOR,
                              UsersTypeEnum.DEAN,
                              UsersTypeEnum.TEACHER,
                              UsersTypeEnum.آموزشگاه,
                              UsersTypeEnum.مرکز_مشاوره,
                              UsersTypeEnum.EXAM_MARKET_PROVIDER,
                              UsersTypeEnum.SUPER_USER
                            )}
                          />
                        }
                      >
                        <Route
                          path="/exam-management/school"
                          element={<ExamManagementSchool />}
                        />

                        <Route
                          path="/exam-launcher/checkout/:archiveId/"
                          element={<ExamLauncherCheckout />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute
                            isAllowed={containsUserType(
                              user?.subTypeId,
                              UsersTypeEnum.ADVISOR,
                              UsersTypeEnum.ADMINISTRATOR,
                              UsersTypeEnum.DEAN,
                              UsersTypeEnum.TEACHER,
                              UsersTypeEnum.آموزشگاه,
                              UsersTypeEnum.مرکز_مشاوره,
                              UsersTypeEnum.STUDENT
                            )}
                          />
                        }
                      >
                        <Route path="/payment/result" element={<Payment />} />
                        <Route
                          path="/activate-contract"
                          element={<ActivateContract />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute
                            isAllowed={containsUserType(
                              user?.subTypeId,
                              UsersTypeEnum.ADVISOR,
                              UsersTypeEnum.DEAN,
                              UsersTypeEnum.آموزشگاه,
                              UsersTypeEnum.مرکز_مشاوره
                            )}
                          />
                        }
                      >
                        <Route
                          path="/manage-descriptive-exams"
                          element={<ManageDescriptiveExamsPage />}
                        />

                        <Route
                          path="/manage-descriptive-exams/submit-marks/:majorId/:gradeId/:stepNo/:primaryProductId?/"
                          element={<SubmitMarksPage />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute
                            isAllowed={containsUserType(
                              user?.subTypeId,
                              UsersTypeEnum.STUDENT
                            )}
                          />
                        }
                      >
                        <Route
                          path="/online-self-exam-generator"
                          element={<OnlineSelfExamGeneratorPage />}
                        />
                      </Route>
                    </Route>

                    <Route element={<EmptyLayout />}>
                      <Route path="/cart/payment" element={<CartPayment />} />
                      <Route
                        path="/auth/consume"
                        element={<LoginWithToken />}
                      />
                      <Route path="/auth">
                        <Route index element={<LoginPage />} />

                        <Route path="forget">
                          <Route index element={<ForgetPassword />} />
                          <Route
                            path="change"
                            element={<ChangePasswordPage />}
                          />
                        </Route>

                        <Route path="login">
                          <Route
                            path="one-time-password"
                            element={<OneTimePassword />}
                          />
                        </Route>

                        <Route path="register">
                          <Route index element={<Register />} />
                          <Route
                            path="phone-number"
                            element={<PhoneNumberPage />}
                          />

                          <Route
                            path="already-signed-up"
                            element={<AlreadySignedUpPage />}
                          />

                          <Route
                            path="completion"
                            element={<CompletionForm />}
                          />
                        </Route>

                        <Route path="otp" element={<OtpPage />} />
                      </Route>

                      <Route path="login">
                        <Route
                          path="one-time-password"
                          element={<OneTimePassword />}
                        />
                      </Route>

                      <Route path="register">
                        <Route index element={<Register />} />
                        <Route
                          path="phone-number"
                          element={<PhoneNumberPage />}
                        />

                        <Route
                          path="already-signed-up"
                          element={<AlreadySignedUpPage />}
                        />

                        <Route path="completion" element={<CompletionForm />} />
                      </Route>

                      <Route path="otp" element={<OtpPage />} />
                    </Route>

                    <Route path="*" element={<Custom404 />} />
                  </Routes>
                </AppJotaiProvider>
              </UserProvider>
            </AuthProvider>

            <OldBrowserModal />
            <ToastProvider />
            {/* <GetUserPhoneModal utils={{ auth, storage, api, cart, strapi }} /> */}
          </QueryClientProvider>
        </LoadingProvider>
      </BrowserRouter>
    </Suspense>
  );
}

export default withPolling(App);
